import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/Icons';
import { formatCharge, formatINR, formatPrice, todayIso } from '../data/common';
import { countries } from '../data/countries';
import { useAuth } from '../../auth/AuthContext';
import { getPriceQuote, listPromoOffers, redeemPromoCode } from '../../api/pricing';
import { collectPayment, getPayment, getPaymentConfig } from '../../api/payments';
import { activeCurrency, isConverted } from '../currency/currencyStore';
import { useBookings } from './BookingsContext';
import { FlowBand, FareBreakdown } from './FlowParts';
import { dobRange, passportMinExpiry, resolveFields } from './travellerRules';

const titleOptions = { Adult: ['Mr', 'Ms', 'Mrs'], Child: ['Master', 'Miss'], Infant: ['Master', 'Miss'] };

// Gender follows the title, so travellers aren't asked for both.
const genderOfTitle = { Mr: 'Male', Master: 'Male', Ms: 'Female', Mrs: 'Female', Miss: 'Female' };

const fieldDefs = {
  title: { label: 'Title', type: 'select' },
  firstName: { label: 'First & middle name', placeholder: 'As on ID / passport' },
  lastName: { label: 'Last name', placeholder: 'As on ID / passport' },
  gender: { label: 'Gender', type: 'select', options: ['Male', 'Female', 'Other'] },
  dob: { label: 'Date of birth', type: 'date' },
  age: { label: 'Age', type: 'number' },
  passport: { label: 'Passport number', placeholder: 'e.g. Z1234567', pattern: '[A-Za-z0-9]{6,9}' },
  pan: { label: 'PAN', placeholder: 'e.g. ABCDE1234F', pattern: '[A-Za-z]{5}[0-9]{4}[A-Za-z]' },
  // Bus operators that check ID ask for the primary passenger's ID proof.
  idType: { label: 'ID proof', type: 'select' },
  idNumber: { label: 'ID number', placeholder: 'As on the ID, no spaces', pattern: '[A-Za-z0-9]{4,20}' },
  passportExpiry: { label: 'Passport expiry', type: 'date', future: true },
  nationality: { label: 'Nationality', type: 'select', options: countries },
  nominee: { label: 'Nominee name', placeholder: 'Full name of nominee' },
  nomineeRelation: {
    label: 'Nominee relationship',
    type: 'select',
    options: [['Spouse', 'Spouse'], ['Parent', 'Parent'], ['Child', 'Child'], ['Sibling', 'Sibling'], ['Guardian', 'Guardian'], ['Friend', 'Friend'], ['LegalHeir', 'Legal heir'], ['Other', 'Other']],
  },
  // Student insurance: course and the sponsor paying for it.
  courseName: { label: 'Course name', placeholder: 'e.g. MSc Computer Science', section: 'Course & sponsor' },
  courseMonths: { label: 'Course length (months)', type: 'number', min: 1, max: 120 },
  university: { label: 'University', placeholder: 'University name' },
  universityCity: { label: 'University city' },
  sponsorName: { label: 'Sponsor name', placeholder: 'Parent or guardian paying fees' },
  sponsorDob: { label: 'Sponsor date of birth', type: 'date', past: true },
  sponsorRelation: { label: 'Sponsor relationship', placeholder: 'e.g. Father' },
  sponsorEmail: { label: 'Sponsor email', type: 'email', placeholder: 'sponsor@example.com' },
};

// Keys match the API's payment methods: card and international go to Razorpay; upi, debit and netbanking to Cashfree.
const paymentMethods = [
  ['upi', 'mobile', 'UPI', 'GPay, PhonePe, Paytm & more'],
  ['card', 'card', 'Credit Card', 'Visa, Mastercard, Amex & RuPay credit cards'],
  ['debit', 'card', 'Debit Card', 'Visa, Mastercard & RuPay debit cards'],
  ['netbanking', 'bank', 'Net Banking', 'All major Indian banks'],
  ['international', 'globe', 'International Card', 'Credit & debit cards issued outside India'],
];

const offerTag = (offer) => (offer.kind === 'Festival' ? `${offer.festivalName} offer` : offer.kind === 'FirstBooking' ? 'First booking' : null);
const formatOfferDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

function TravellerFields({ spec, value, onChange, index, international, travelDate, travelEnd }) {
  const set = (k) => (e) => onChange({
    ...value,
    [k]: e.target.value,
    ...(k === 'title' && genderOfTitle[e.target.value] ? { gender: genderOfTitle[e.target.value] } : {}),
  });
  const all = resolveFields(spec, international);
  const fields = all.includes('title') ? all.filter((k) => k !== 'gender') : all;
  const dob = dobRange(spec, travelDate);
  const dateLimits = {
    dob: { min: dob.min, max: dob.max, hint: dob.hint },
    passportExpiry: { min: passportMinExpiry(travelEnd || travelDate), hint: 'Valid for 6+ months after travel' },
    sponsorDob: { max: todayIso() },
  };
  return (
    <div className="traveller-card">
      <div className="traveller-head">
        <span className="traveller-num">{index + 1}</span>
        <b>{spec.label}</b>
        {spec.note && <span className="muted small">{spec.note}</span>}
      </div>
      <div className="form-grid">
        {fields.map((k) => {
          const def = fieldDefs[k];
          const required = k !== 'nominee' || spec.required?.includes(k);
          const section = def.section && <p key={`${k}-section`} className="field-section">{def.section}</p>;
          if (def.type === 'select') {
            const opts = spec.options?.[k] || (k === 'title' ? titleOptions[spec.type] || titleOptions.Adult : def.options);
            return (
              <label key={k} className={`field ${k === 'title' ? 'field-sm' : ''}`}>
                <span className="field-label">{def.label}</span>
                <select required value={value[k] || ''} onChange={set(k)}>
                  <option value="" disabled>Select</option>
                  {opts.map((o) => {
                    const [optValue, optLabel] = Array.isArray(o) ? o : [o, o];
                    return <option key={optValue} value={optValue}>{optLabel}</option>;
                  })}
                </select>
              </label>
            );
          }
          const limits = dateLimits[k] || spec.limits?.[k];
          const field = (
            <label key={k} className="field">
              <span className="field-label">{def.label}</span>
              <input required={required} type={def.type || 'text'} value={value[k] || ''} onChange={set(k)} placeholder={def.placeholder}
                pattern={def.pattern} title={limits?.hint}
                min={limits ? limits.min : def.type === 'number' ? def.min ?? 0 : undefined}
                max={limits ? limits.max : def.max} />
              {limits?.hint && <span className="field-hint">{limits.hint}</span>}
            </label>
          );
          return section ? [section, field] : field;
        })}
      </div>
    </div>
  );
}

/**
 * Shared "Review & Pay" step used by every module.
 * Modules supply the summary card, traveller specs, fare lines and a buildBooking() callback;
 * `travellersIntro` renders above the traveller forms (e.g. an option that changes which fields they show).
 * `international` adds passport details for every traveller; `travelDate` / `travelEnd`
 * drive the date-of-birth and passport-expiry checks (see travellerRules.js).
 */
export default function ReviewPage({ module, heading, back, summary, info = [], travellers: specs, travellersIntro, fareLines, extra, buildBooking, onSubmit, submitLabel, submitNotice, showPayment = true, showCoupons = true, showGst = true, requireGstAddress = false, international = false, travelDate, travelEnd, initialContact, contactNote, afterTravellers }) {
  const navigate = useNavigate();
  const { addBooking } = useBookings();
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const [travellers, setTravellers] = useState(() => specs.map((s) => ({ type: s.type, nationality: resolveFields(s, international).includes('nationality') ? 'IN' : undefined })));
  const [contact, setContact] = useState({ email: '', phone: '', gst: false, gstin: '', company: '', ...initialContact });
  const [code, setCode] = useState('');
  const [promo, setPromo] = useState(null);
  const [msg, setMsg] = useState(null);
  const [promoOffers, setPromoOffers] = useState([]);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(true);
  const [quoteError, setQuoteError] = useState('');
  const [requote, setRequote] = useState(0);
  const [payment, setPayment] = useState('upi');
  // Methods the API can take payment with; null while loading, [] when no gateway is set up (demo checkout).
  const [onlineMethods, setOnlineMethods] = useState(null);
  // A payment that went through but whose booking failed before using it; reused if the total is unchanged.
  const paidRef = useRef(null);
  const [agree, setAgree] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [paying, setPaying] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // Read when pricing, without re-pricing on every keystroke; first-booking offers need the contact email.
  const emailRef = useRef(contact.email);
  emailRef.current = contact.email;

  // Only checkouts that book with a supplier (onSubmit) take real payment.
  const checksPayment = Boolean(onSubmit) && showPayment;
  const payOnline = checksPayment && onlineMethods?.length > 0;
  const visibleMethods = payOnline ? paymentMethods.filter(([k]) => onlineMethods.includes(k)) : paymentMethods;
  // Supplier checkouts are priced for the chosen method: its transaction fee is part of the convenience fee.
  const quoteMethod = onSubmit ? payment : null;

  useEffect(() => {
    if (!checksPayment) return undefined;
    const controller = new AbortController();
    getPaymentConfig(controller.signal)
      .then((config) => {
        setOnlineMethods(config.methods || []);
        if (config.methods?.length) setPayment((current) => (config.methods.includes(current) ? current : config.methods[0]));
      })
      .catch((error) => { if (error.name !== 'AbortError') setOnlineMethods([]); });
    return () => controller.abort();
  }, [checksPayment]);

  const subtotal = fareLines.reduce((s, [, a]) => s + a, 0);
  const travellerCount = specs.length;

  useEffect(() => {
    if (!showCoupons) return undefined;
    const controller = new AbortController();
    listPromoOffers(module, controller.signal).then(setPromoOffers).catch(() => setPromoOffers([]));
    return () => controller.abort();
  }, [module, showCoupons]);

  // The convenience fee and promo discount come from the API so they match what admins configured.
  useEffect(() => {
    const controller = new AbortController();
    setQuoting(true);
    getPriceQuote({ module, subtotal, travellers: travellerCount, promoCode: promo, email: emailRef.current.trim(), paymentMethod: quoteMethod }, token, controller.signal)
      .then((result) => {
        setQuote(result);
        setQuoteError('');
        if (promo) setMsg({ ok: true, text: `${result.promoCode} applied — you save ${formatPrice(result.discount)}` });
        setQuoting(false);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        if (promo && error.status === 400) {
          // Rejected code (expired, first booking only, minimum value…): show why and price without it.
          setMsg({ ok: false, text: error.message });
          setPromo(null);
          return;
        }
        setQuoteError(error.message);
        setQuoting(false);
      });
    return () => controller.abort();
  }, [module, subtotal, travellerCount, promo, token, requote, quoteMethod]);

  const ready = Boolean(quote) && !quoting && !quoteError && (!checksPayment || onlineMethods !== null);
  const fare = {
    lines: fareLines,
    subtotal,
    taxes: fareLines.filter((l) => l[2] === 'tax').reduce((s, l) => s + l[1], 0),
    tcs: fareLines.filter((l) => l[2] === 'tcs').reduce((s, l) => s + l[1], 0),
    convenienceFee: quote?.convenienceFee || 0,
    convenienceFeeNote: quote?.convenienceFeeNote || null,
    discount: quote?.discount || 0,
    coupon: quote?.promoCode || null,
    total: quote ? quote.total : subtotal,
  };

  const applyCoupon = (value = code) => {
    const key = value.trim().toUpperCase();
    setCode(key);
    if (!key) return;
    setMsg(null);
    if (key === promo) setRequote((n) => n + 1);
    else setPromo(key);
  };

  const removeCoupon = () => {
    setPromo(null);
    setCode('');
    setMsg(null);
  };

  // Pays fare.total through the gateway for the chosen method, or reuses an unused payment for the same total and method.
  const takePayment = async () => {
    const previous = paidRef.current;
    if (previous?.amount === fare.total && previous.method === payment) {
      const status = await getPayment(previous.id).catch(() => null);
      if (status?.status === 'Paid') return previous.id;
    }
    paidRef.current = null;
    setPaying(true);
    try {
      const id = await collectPayment({ module, method: payment, amount: fare.total, contact }, token);
      paidRef.current = { id, amount: fare.total, method: payment };
      return id;
    } finally {
      setPaying(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!agree || !ready) return;
    setProcessing(true);
    setSubmitError('');
    let stage = 'booking';
    try {
      let paidFare = fare;
      let submittedBooking = {};
      if (onSubmit) {
        let paymentId;
        if (payOnline) {
          stage = 'payment';
          paymentId = await takePayment();
          stage = 'booking';
        }
        submittedBooking = await onSubmit({ travellers, contact, fare, payment, promoCode: fare.coupon, paymentId });
        paidRef.current = null;
      } else {
        await new Promise((resolve) => setTimeout(resolve, 1400));
        if (fare.coupon) {
          // Re-checked with the final contact email and recorded, so limits and first-booking offers hold.
          const redeemed = await redeemPromoCode({
            module, subtotal, travellers: travellerCount, promoCode: fare.coupon, email: contact.email.trim(),
          }, token);
          paidFare = { ...fare, convenienceFee: redeemed.convenienceFee, discount: redeemed.discount, total: redeemed.total };
        }
      }
      const booking = addBooking({
        module,
        ...buildBooking({ travellers, contact }),
        ...submittedBooking,
        // Bookings are kept in this browser, so only the last digits of a PAN or ID number are saved.
        travellers: travellers.map((t) => ({
          ...t,
          ...(t.pan ? { pan: `XXXXX${t.pan.slice(5).toUpperCase()}` } : {}),
          ...(t.idNumber ? { idNumber: `XXXX${t.idNumber.slice(-4).toUpperCase()}` } : {}),
        })),
        contact: { email: contact.email, phone: contact.phone, ...(contact.gst ? { gstin: contact.gstin, company: contact.company } : {}) },
        fare: paidFare,
        payment,
      });
      navigate(`/${module}/confirmation/${booking.id}`, { replace: true });
    } catch (error) {
      if (stage === 'payment') {
        setSubmitError(error.message || 'The payment could not be completed. Please try again.');
      } else if (paidRef.current && (error.status === 502 || error.status === 503 || !error.status)) {
        setSubmitError(`${error.message || 'Unable to confirm this booking.'} The amount paid will be refunded to your original payment method within 5–7 working days.`);
      } else if (fare.coupon && error.status === 400 && !/payment/i.test(error.message || '')) {
        removeCoupon();
        setSubmitError(`${error.message} The offer has been removed — please check the new total and pay again.`);
      } else if (error.status === 409) {
        setRequote((n) => n + 1);
        setSubmitError(error.message);
      } else {
        setSubmitError(error.message || 'Unable to submit this booking. Please try again.');
      }
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <FlowBand title={heading} step={2} back={back} />
      <form className="container checkout" onSubmit={submit}>
        <div className="checkout-main">
          <section className="panel">{summary}</section>

          {info.length > 0 && (
            <section className="panel info-panel">
              <h3><Icon name="file" size={18} /> Important information</h3>
              <ul>{info.map((i) => <li key={i}>{i}</li>)}</ul>
            </section>
          )}

          <section className="panel">
            <h3><Icon name="user" size={18} /> Traveller details</h3>
            <p className="muted small form-note">Please enter names exactly as they appear on government ID / passport.</p>
            {international && (
              <div className="notice doc-notice">
                <Icon name="file" size={18} />
                <span><b>International trip:</b> passport details are mandatory for all travellers. The passport must be valid for at least 6 months after your travel dates.</span>
              </div>
            )}
            {travellersIntro}
            {specs.map((s, i) => (
              <TravellerFields key={i} index={i} spec={s} value={travellers[i]}
                international={international} travelDate={travelDate} travelEnd={travelEnd}
                onChange={(v) => setTravellers((list) => list.map((t, j) => (j === i ? v : t)))} />
            ))}
            {extra}
          </section>

          {afterTravellers}

          <section className="panel">
            <h3><Icon name="phone" size={18} /> Contact details</h3>
            <p className="muted small form-note">Your ticket / voucher will be sent here.</p>
            {contactNote}
            <div className="form-grid">
              <label className="field"><span className="field-label">Email address</span>
                <input required type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="you@example.com" /></label>
              <label className="field"><span className="field-label">Mobile number</span>
                <input required type="tel" pattern="[0-9+ ]{10,15}" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="+91 98765 43210" /></label>
            </div>
            {showGst && (
              <label className="check gst-check">
                <input type="checkbox" checked={contact.gst} onChange={(e) => setContact({ ...contact, gst: e.target.checked })} />
                <span>I have a GST number (optional)</span>
              </label>
            )}
            {showGst && contact.gst && (
              <div className="form-grid">
                <label className="field"><span className="field-label">GSTIN</span>
                  <input required pattern="[0-9A-Za-z]{15}" value={contact.gstin} onChange={(e) => setContact({ ...contact, gstin: e.target.value })} placeholder="15-character GSTIN" /></label>
                <label className="field"><span className="field-label">Company name</span>
                  <input required value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} /></label>
                {requireGstAddress && <label className="field"><span className="field-label">Registered address</span>
                  <input required value={contact.gstAddress || ''} onChange={(e) => setContact({ ...contact, gstAddress: e.target.value })} /></label>}
              </div>
            )}
          </section>

          {showPayment && (
            <section className="panel">
              <h3><Icon name="wallet" size={18} /> Payment method</h3>
              <div className="pay-options">
                {visibleMethods.map(([k, ic, l, d]) => (
                  <label key={k} className={`pay-option ${payment === k ? 'active' : ''}`}>
                    <input type="radio" name="pay" checked={payment === k} onChange={() => setPayment(k)} />
                    <Icon name={ic} size={22} /><span><b>{l}</b><small>{d}</small></span>
                  </label>
                ))}
              </div>
            </section>
          )}

          <label className="check agree">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} required />
            <span>I confirm that traveller details are correct and I accept the <a href="/terms" target="_blank" rel="noopener" className="link">booking terms</a>, <a href="/cancellation-policy" target="_blank" rel="noopener" className="link">cancellation policy</a> and <a href="/privacy-policy" target="_blank" rel="noopener" className="link">privacy policy</a>.</span>
          </label>
          {ready && isConverted() && (
            <div className="notice currency-notice">
              <Icon name="globe" size={18} />
              <span>Prices are shown in <b>{activeCurrency().code}</b> using our exchange rate. Your payment is processed in <b>INR</b>; your bank may apply its own conversion.</span>
            </div>
          )}
          {ready && (
            <p className="muted small pay-breakdown">
              You pay <b>{formatCharge(fare.total)}</b>
              {fare.convenienceFee > 0 && <> · includes {formatPrice(fare.convenienceFee)} convenience fee</>}
              {fare.discount > 0 && <> · {formatPrice(fare.discount)} off with {fare.coupon}</>}
            </p>
          )}
          <div className="pay-row">
            <button className="btn btn-primary lg" disabled={processing || !ready}>
              {processing ? (paying ? 'Waiting for payment…' : 'Submitting…') : !ready ? 'Calculating total…' : submitLabel || `Pay ${formatINR(fare.total)}`}
            </button>
            <span className="muted small secure-note"><Icon name="lock" size={13} /> {payOnline
              ? `Secure payment by ${payment === 'card' || payment === 'international' ? 'Razorpay' : 'Cashfree'}. ${submitNotice || ''}`
              : `${onSubmit ? 'Demo payment — no money is taken. ' : ''}${submitNotice || 'Demo checkout — no real payment is taken.'}`}</span>
          </div>
          {submitError && <div className="api-message error" role="alert">{submitError}</div>}
        </div>

        <aside className="checkout-side">
          <div className="panel">
            <h3>Fare summary</h3>
            {quoteError ? (
              <div className="api-message error" role="alert">
                <p>We couldn't calculate the final price: {quoteError}</p>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setRequote((n) => n + 1)}>Try again</button>
              </div>
            ) : (
              <div className={quoting ? 'fare-updating' : undefined} aria-busy={quoting}>
                <FareBreakdown fare={fare} />
              </div>
            )}
          </div>
          {showCoupons && (
            <div className="panel">
              <h3><Icon name="tag" size={18} /> Offers & promo codes</h3>
              <div className="coupon-box">
                <input aria-label="Promo code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter promo code"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyCoupon(); } }} />
                {fare.coupon && fare.coupon === code
                  ? <button type="button" className="btn btn-outline" onClick={removeCoupon}>Remove</button>
                  : <button type="button" className="btn btn-outline" onClick={() => applyCoupon()} disabled={quoting && Boolean(promo)}>Apply</button>}
              </div>
              {msg && <p className={`small ${msg.ok ? 'text-primary' : 'text-coral'}`} role="status">{msg.text}</p>}
              {promoOffers.map((o) => (
                <label key={o.code} className={`coupon-option ${fare.coupon === o.code ? 'active' : ''}`}>
                  <input type="radio" name="coupon" checked={promo === o.code} onChange={() => applyCoupon(o.code)} />
                  <span>
                    <b>{o.code}{offerTag(o) && <em className="offer-tag">{offerTag(o)}</em>}</b>
                    <small>{o.title}</small>
                    {o.description && <small>{o.description}</small>}
                    <small>Valid till {formatOfferDate(o.validUntil)}{o.minBookingAmount ? ` · Min. booking ${formatPrice(o.minBookingAmount)}` : ''}</small>
                  </span>
                </label>
              ))}
            </div>
          )}
        </aside>
      </form>

      {/* Hidden while the gateway's own payment window is open. */}
      {processing && !paying && (
        <div className="overlay">
          <div className="overlay-card">
            <span className="spinner" />
            <b>{payOnline ? 'Payment received — confirming your booking…' : 'Processing your payment…'}</b>
            <span className="muted small">Please don’t refresh or press back.</span>
          </div>
        </div>
      )}
    </>
  );
}
